import moment from "moment";
import * as React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useQuery, useMutation } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";

import { symbols } from "./symbols-arr";
import { GET_PUMPS, CREATE_PUMP_REQUEST } from "../graphql/pumps";
import { FAV_COINS, ADD_FAV_COIN, REMOVE_FAV_COIN } from "../graphql/fav-coins";

import ProcessedPumps from "./ProcessedPumps";
import ProcessingPumps from "./ProcessingPumps";

export default function PumpsPage({}) {
	const [isAlertOpen, setIsAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [search, setSearch] = React.useState("");
	const [finishDate, setFinishDate] = React.useState(
		moment().format("YYYY-MM-DD")
	);
	const [accumulationLength, setAccumulationLength] = React.useState(45);
	const [volumeSpikes, setVolumeSpikes] = React.useState(4);
	const [scopeLength, setScopeLength] = React.useState(400);
	const [selectedSymbols, setSelectedSymbols] = React.useState([]);

	const {
		loading: favCoinsLoading,
		error: favCoinsError,
		data: favCoinsData,
		refetch: refetchFavCoins,
	} = useQuery(FAV_COINS, {
		fetchPolicy: "network-only",
		variables: {},
	});
	console.log("favCoinsArr", favCoinsData?.favCoins);
	const {
		loading: processingPumpsLoading,
		error: processingPumpsError,
		data: processingPumps,
		refetch,
	} = useQuery(GET_PUMPS, {
		pollInterval: 5000,
		fetchPolicy: "network-only",
		variables: {
			input: { statuses: ["PROCESSING"], symbolSearch: "" },
		},
	});

	const {
		loading: processedFoundPumpsLoading,
		error: processedFoundPumpsError,
		data: processedFoundPumps,
		refetch: refetchFoundPump,
	} = useQuery(GET_PUMPS, {
		pollInterval: 5000,
		fetchPolicy: "network-only",
		variables: {
			input: { statuses: ["FINISHED"], symbolSearch: search },
		},
	});

	const {
		loading: processedNotFoundPumpsLoading,
		error: processedNotFoundPumpsError,
		data: processedNotFoundPumps,
		refetch: refetchNotFoundPump,
	} = useQuery(GET_PUMPS, {
		pollInterval: 5000,
		fetchPolicy: "network-only",
		variables: {
			input: { statuses: ["FAILED"], symbolSearch: search },
		},
	});

	const [
		createPumpRequest,
		{
			loading: createPumpRequestLoading,
			error: createPumpRequestError,
			data: createPumpRequestData,
		},
	] = useMutation(CREATE_PUMP_REQUEST);

	const createRequest = async () => {
		await createPumpRequest({
			variables: {
				symbols: selectedSymbols,
				sigmaThreshold: 4,
				minPumpLength: accumulationLength,
				minVolumeSpikes: volumeSpikes,
				finishDate: finishDate,
				klinesAmount: scopeLength,
			},
		}).then((res) => {
			setAlertMessage(
				`Search for ${selectedSymbols.length} coins successfully launched`
			);
			setIsAlertOpen(true);
		});
	};

	const [
		addFavCoin,
		{
			loading: addFavCoinLoading,
			error: addFavCoinError,
			data: addFavCoinData,
		},
	] = useMutation(ADD_FAV_COIN);
	const processAddFavCoin = async (coin) =>
		addFavCoin({
			variables: {
				coin,
			},
		}).then(() => refetchFavCoins());
	const [
		removeFavCoin,
		{
			loading: removeFavCoinLoading,
			error: removeFavCoinError,
			data: removeFavCoinData,
		},
	] = useMutation(REMOVE_FAV_COIN);
	const processRemoveFavCoin = async (coin) =>
		removeFavCoin({
			variables: {
				coin,
			},
		}).then(() => refetchFavCoins());

	const isLoading = () =>
		processingPumpsLoading ||
		processedFoundPumpsLoading ||
		processedNotFoundPumpsLoading ||
		createPumpRequestLoading ||
		addFavCoinLoading ||
		removeFavCoinLoading ||
		favCoinsLoading;

	const onSearchQueryUpdated = async (newV, status) => {
		setSearch(newV);
		if (status === "FOUND") {
			refetchFoundPump({
				input: {
					statuses: ["FINISHED"],
					symbolSearch: newV,
				},
			});
		} else if (status === "NOT_FOUND") {
			refetchNotFoundPump({
				variables: {
					statuses: ["FAILED"],
					symbolSearch: newV,
				},
			});
		}
	};

	return (
		<>
			<div>
				<Paper
					sx={{
						padding: 2,
					}}
				>
					<Box>
						<Box
							style={{
								display: "flex",
								justifyContent: "flex-start",
								alignItems: "center",
								margin: 10,
								gap: 15,
							}}
						>
							<div>
								<TextField
									id="outlined-basic"
									label="Accumulation"
									type="number"
									variant="outlined"
									sx={{ width: 100 }}
									value={accumulationLength}
									onChange={(event) =>
										setAccumulationLength(event.target.value)
									}
								/>
							</div>
							<div>
								<TextField
									id="volume-spikes"
									label="Volumes"
									type="number"
									variant="outlined"
									sx={{ width: 100 }}
									value={volumeSpikes}
									onChange={(event) => setVolumeSpikes(event.target.value)}
								/>
							</div>
							<div>
								<TextField
									id="finish-date"
									required={true}
									label="Finish Date"
									variant="outlined"
									sx={{ width: 150 }}
									value={finishDate}
									onChange={(event) => setFinishDate(event.target.value)}
								/>
							</div>
							<div>
								<TextField
									id="scope-length"
									label="Scope length"
									type="number"
									variant="outlined"
									sx={{ width: 100 }}
									value={scopeLength}
									onChange={(event) => setScopeLength(+event.target.value)}
								/>
							</div>
							<Box
								sx={{
									minWidth: 500,
									maxWidth: 500,
									maxHeight: 110,
								}}
							>
								<Autocomplete
									sx={{
										maxHeight: 110,
										minHeight: 110,
										margin: "auto",
										height: 110,
									}}
									multiple
									options={symbols}
									getOptionLabel={(option) => option}
									id="clear-on-escape"
									clearOnEscape
									value={selectedSymbols}
									onChange={(e, selectedValue) => {
										setSelectedSymbols((oldV) => {
											const newOnes = selectedValue.filter(
												(x) => !oldV.includes(x)
											);
											return oldV.concat(newOnes);
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Symbols(select at least one)"
											variant="standard"
										/>
									)}
									renderTags={(tagValue, getTagProps) => (
										<Box
											sx={{
												display: "flex",
												overflowX: "auto",
											}}
										>
											{tagValue.map((option, index) => (
												<Chip
													label={option}
													{...getTagProps({ index })}
													onDelete={() =>
														setSelectedSymbols((oldV) =>
															oldV.filter((x) => x !== option)
														)
													}
												/>
											))}
										</Box>
									)}
								/>
							</Box>
							<Box
								sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
							>
								<Button
									onClick={() => {
										setSelectedSymbols([]);
									}}
									variant="contained"
								>
									remove all
								</Button>
								<Button
									onClick={() => {
										setSelectedSymbols(symbols);
									}}
									variant="contained"
								>
									select all
								</Button>
							</Box>
							<div style={{}}>
								<Button
									onClick={createRequest}
									color={"primary"}
									variant="contained"
									disabled={!finishDate || selectedSymbols.length < 1}
								>
									RUN
									<PlayCircleFilledIcon />
								</Button>
							</div>
						</Box>
					</Box>
				</Paper>
				<div
					style={{
						display: "flex",
						height: "100%",
						gap: "10px",
						margin: "10px",
					}}
				>
					<ProcessingPumps processingPumps={processingPumps?.pumps} />
					<ProcessedPumps
						processedFoundPumps={processedFoundPumps?.pumps}
						processedNotFoundPumps={processedNotFoundPumps?.pumps}
						setSearch={onSearchQueryUpdated}
						favIconsArr={
							favCoinsData && favCoinsData.favCoins ? favCoinsData.favCoins : []
						}
						addFavCoin={(coin) => processAddFavCoin(coin)}
						removeFavCoin={(coin) => processRemoveFavCoin(coin)}
					/>
				</div>
			</div>
			<Snackbar
				ContentProps={{
					classes: {
						root: {
							background: "blue",
						},
					},
				}}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={isAlertOpen}
				onClose={() => setIsAlertOpen(false)}
				message={alertMessage}
				key={"snack-bar-success"}
				autoHideDuration={5000}
				color="success"
				variant="contained"
			>
				<Alert
					severity="success"
					variant="filled"
					onClose={() => setIsAlertOpen(false)}
				>
					{alertMessage}
				</Alert>
			</Snackbar>
			{isLoading() && (
				<CircularProgress
					size={24}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
					}}
				/>
			)}
		</>
	);
}
